import enquire from 'enquire.js'
import delegate from 'delegate'

// helpers
import Cache from './Cache'
import Link from './Link'
import transitionEnd from './transitionEnd'

// modules
import request from './modules/request'
import getDataFromHtml from './modules/getDataFromHtml'
import loadPage from './modules/loadPage'
import renderPage from './modules/renderPage'
import goBack from './modules/goBack'
import createState from './modules/createState'
import triggerEvent from './modules/triggerEvent'
import getUrl from './modules/getUrl'
import scrollTo from './modules/scrollTo'
import classify from './modules/classify'
import doScrolling from './modules/doScrolling'

export default class Xhr2 {
    constructor(setOptions) {
        // default options
        let defaults = {
            cache: true,
            animationSelector: '[class^="a-"]',
            elements: ['#xhr'],
            pageClassPrefix: '',
            specialPages: [],
            debugMode: false,
            scroll: true,
            preload: true,
            support: true,
            animateScrollToAnchor: false,
            animateScrollOnMobile: false,
            doScrollingRightAway: false,
            LINK_SELECTOR: 'a[href^="/"]:not([data-no-xhr2]), a[href^="#"]:not([data-no-xhr2]), a[xlink\\:href]'
        }

        let options = Object.assign({}, defaults, setOptions);

        // helper variables
        this.mobile = false
        this.scrollToElement = null
        this.preloadPromise = null

        this.LINK_SELECTOR = options.LINK_SELECTOR
        this.debugMode = options.debugMode
        this.specialPages = options.specialPages
        this.animationSelector = options.animationSelector
        this.elements = options.elements
        this.scroll = options.scroll
        this.preload = options.preload
        this.pageClassPrefix = options.pageClassPrefix
        this.animateScrollToAnchor = options.animateScrollToAnchor
        this.doScrollingRightAway = options.doScrollingRightAway
        this.animateScrollOnMobile = options.animateScrollOnMobile

        // make accessible in instance
        this.getUrl = getUrl
        this.cache = new Cache()
        this.link = Link
        this.transitionEnd = transitionEnd()
        this.getDataFromHtml = getDataFromHtml
        this.getPage = request
        this.scrollTo = scrollTo
        this.loadPage = loadPage
        this.renderPage = renderPage
        this.goBack = goBack
        this.createState = createState
        this.triggerEvent = triggerEvent
        this.classify = classify
        this.doScrolling = doScrolling

        // save options
        this.options = options

        // detect mobile devices
        if (this.scroll) {
            enquire.register('screen and (max-width: 47.9375em)', {
                match: () => {
                    this.mobile = true
                },
                unmatch: () => {
                    this.mobile = false
                }
            })
        }

        if (this.debugMode) {
            window.xhr2 = this
        }

        this.getUrl()
        this.prepare()
    }

    prepare() {
        // support check
        if (this.support) {
            // check pushState support
            if (!('pushState' in window.history)) {
                console.warn('pushState is not supported')
                return
            }
            // check transitionEnd support
            if (transitionEnd()) {
                this.transitionEnd = transitionEnd()
            } else {
                console.warn('transitionEnd detection is not supported')
                return
            }
            // check Promise support
            if(typeof Promise === "undefined" || Promise.toString().indexOf("[native code]") === -1){
                console.warn('Promise is not supported')
                return
            }
        }

        // link click listener
        delegate(document, this.LINK_SELECTOR, 'click', event => {
            // no control key pressed
            if (!event.metaKey) {
                this.triggerEvent('clickLink')
                var link = new Link()
                event.preventDefault()
                link.setPath(event.delegateTarget.href)
                if (link.getPath() == this.currentUrl || link.getPath() == '') {
                    if (link.getHash() != '') {
                        this.triggerEvent('samePageWithHash')
                        var element = document.querySelector(link.getHash())
                        if (element != null) {
                            if (this.scroll) {
                                this.scrollTo(document.body, element.offsetTop, 320)
                            }
                            history.replaceState(undefined, undefined, link.getHash())
                        } else {
                            console.warn(`Element for offset not found (${link.getHash()})`)
                        }
                    } else {
                        this.triggerEvent('samePage')
                        if (this.scroll) {
                            this.scrollTo(document.body, 0, 320)
                        }
                    }
                } else {
                    if (link.getHash() != '') {
                        this.scrollToElement = link.getHash()
                    }
                    this.loadPage(link.getPath(), false)
                }
            } else {
                this.triggerEvent('openPageInNewTab')
            }
        });

        // link mouseover listener
    delegate(document.body, this.LINK_SELECTOR, 'mouseover', event => {
            this.triggerEvent('hoverLink')
            if (this.preload) {
                var link = new Link()
                link.setPath(event.delegateTarget.href)
                if (link.getPath() != this.currentUrl && !this.cache.exists(link.getPath()) && this.preloadPromise == null) {
                    this.preloadPromise = new Promise(resolve => {
                        this.getPage(link.getPath(), response => {
                            if (response === null) {
                                console.warn('Server error.')
                                this.triggerEvent('serverError')
                            } else {
                                this.triggerEvent('pagePreloaded')
                                // get json data
                                var page = getDataFromHtml(response)
                                page.url = link.getPath()
                                this.cache.cacheUrl(page, this.debugMode)
                            }
                            resolve()
                            this.preloadPromise = null
                        })
                    })
                    this.preloadPromise.route = link.getPath()
                }
            }
        });

        // forms submit listeners
        //this.xhrForms.on('submit', function (event) {
        //    event.preventDefault();
        //    self.current = $(this).attr('action');
        //    var finalPage = null;
        //
        //    // handle classes and events before unload
        //    self.animateOut();
        //
        //    self.$body.addClass('show-loading');
        //    $.ajax({
        //        url: $(this).attr('action'),
        //        type: $(this).attr('method'),
        //        data: $(this).serialize(),
        //        success: (response, status, xhr) => {
        //            // get json data
        //            var ct = xhr.getResponseHeader("content-type") || "";
        //            if (ct.indexOf('html') > -1) {
        //                var page = getDataFromHtml(response);
        //            }
        //            if (ct.indexOf('json') > -1) {
        //                var page = response;
        //                if (response.redirect !== null) {
        //                    self.loadPage(response.redirect, event);
        //                    return;
        //                }
        //            }
        //            // render page
        //            finalPage = page;
        //            self.renderPage(finalPage, event);
        //        }
        //    });
        //});

        // popstate
        window.onpopstate = (event) => {
            var link = new Link();
            link.setPath(event.state ? event.state.url : window.location.pathname);
            if (link.getHash() != '') {
                this.scrollToElement = link.getHash();
            } else {
                event.preventDefault();
            }
            this.triggerEvent('popState');
            this.loadPage(link.getPath(), event);
        };

        // initial save to cache
        var page = this.getDataFromHtml(document.documentElement.innerHTML);
        page.url = this.currentUrl;
        if (this.options.cache) {
            this.cache.cacheUrl(page, this.debugMode)
        }
        this.triggerEvent('pageView')
    }
}


// WEBPACK FOOTER //
// ./core/xhr2/index.js